import { useEffect, useRef, useState } from 'react';
import { Link2, Loader, RefreshCw, Search, Sparkles, AlertCircle } from 'lucide-react';
import Modal from './Modal.jsx';
import { apiGescomExport } from '../api/client.js';

/**
 * Lie une affaire Synolog à son projet Gescom (`Affaire.gescomProjectId`).
 *
 * Ouvert depuis les lignes bloquées de l'Export Gescom. Propose d'abord les
 * projets dont le libellé contient le code CH de l'affaire (même clé que
 * l'arbitrage des transcodifications de synosight), puis une recherche libre
 * dans le catalogue Gescom (`/api/ext/projects`). Rien n'est lié sans clic.
 *
 * Deux usages :
 *  - sans `onPick` (Export Gescom) : le clic enregistre aussitôt la liaison
 *    (`PUT …/gescom-project`) puis appelle `onLinked` ;
 *  - avec `onPick` (fiche affaire) : le clic renvoie seulement le projet
 *    choisi ; la fiche le pose dans son formulaire, enregistré avec le reste.
 *    `affaire.id` peut alors être absent (création).
 */

const pill = (active) => ({
  padding: '4px 12px', borderRadius: 999, border: '1px solid',
  borderColor: active ? 'var(--accent)' : 'var(--glass-border)',
  background: active ? 'var(--accent)' : 'transparent',
  color: active ? '#fff' : 'var(--text-muted)', cursor: 'pointer', fontSize: 12.5,
});

function ProjectRow({ p, currentId, affaireId, saving, onPick }) {
  const isCurrent = currentId && p.gescomProjectId === currentId;
  const others = (p.linkedAffaires ?? []).filter((a) => a.id !== affaireId);
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
        borderBottom: '1px solid var(--glass-border)',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: 'var(--text)', overflowWrap: 'anywhere' }}>
          {p.libelle}
          {!p.isActive ? (
            <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--warning)' }}>Inactif</span>
          ) : null}
          {p.isInternal ? (
            <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-muted)' }}>Interne</span>
          ) : null}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
          ProjectId {p.gescomProjectId}
          {p.codeAff ? ` · aff. ${p.codeAff}` : ''}
          {p.codeProjet ? ` · projet ${p.codeProjet}` : ''}
        </div>
        {others.length > 0 ? (
          <div style={{ fontSize: 11.5, color: 'var(--warning)', marginTop: 2 }}>
            Déjà lié à : {others.map((a) => a.name).join(', ')}
          </div>
        ) : null}
      </div>
      {isCurrent ? (
        <span style={{ fontSize: 12, color: 'var(--success)' }}>Lié</span>
      ) : (
        <button
          type="button"
          disabled={saving}
          onClick={() => onPick(p)}
          title={`Lier l’affaire au projet Gescom ${p.gescomProjectId}`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px',
            borderRadius: 'var(--radius-sm)', border: '1px solid var(--accent)',
            background: 'transparent', color: 'var(--accent)', fontSize: 12.5,
            cursor: saving ? 'wait' : 'pointer', flexShrink: 0,
          }}
        >
          <Link2 size={13} /> Lier
        </button>
      )}
    </div>
  );
}

export default function GescomProjectLinkModal({ affaire, onClose, onLinked, onPick, sessionId }) {
  const [query, setQuery] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const reqRef = useRef(0);

  const load = async ({ refresh = false } = {}) => {
    const n = ++reqRef.current;
    setLoading(true);
    setError(null);
    try {
      const data = await apiGescomExport.catalog({
        name: affaire.name,
        projectId: affaire.gescomProjectId || undefined,
        q: query.trim() || undefined,
        includeInactive,
        refresh,
      });
      if (n === reqRef.current) setInfo(data);
    } catch (e) {
      if (n === reqRef.current) setError(e.message || 'Catalogue Gescom indisponible');
    } finally {
      if (n === reqRef.current) setLoading(false);
    }
  };

  // Recherche débouncée ; premier chargement immédiat (suggestions).
  useEffect(() => {
    const t = setTimeout(() => { void load(); }, info ? 250 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, includeInactive]);

  const pick = async (p) => {
    const others = (p.linkedAffaires ?? []).filter((a) => a.id !== affaire.id);
    if (others.length > 0 && !window.confirm(
      `Ce projet Gescom est déjà lié à : ${others.map((a) => a.name).join(', ')}.\n\nLier aussi « ${affaire.name} » ?`,
    )) return;
    if (onPick) {
      onPick(p);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await apiGescomExport.linkAffaire(affaire.id, p.gescomProjectId, sessionId);
      onLinked?.({ affaireId: affaire.id, gescomProjectId: p.gescomProjectId, libelle: p.libelle });
    } catch (e) {
      setError(e.message || 'Liaison impossible');
      setSaving(false);
    }
  };

  const currentId = info?.current?.gescomProjectId ?? null;
  const suggestions = info?.suggestions ?? [];
  const results = (info?.results ?? []).filter(
    (r) => !suggestions.some((s) => s.gescomProjectId === r.gescomProjectId),
  );

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title="Lier à un projet Gescom"
      subtitle={affaire.name}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {info ? `${info.catalogSize} projets dans le catalogue Gescom` : ''}
          </span>
          <button
            type="button" onClick={onClose}
            style={{ padding: '6px 14px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)', background: 'transparent', color: 'var(--text)', cursor: 'pointer' }}
          >
            Annuler
          </button>
        </div>
      )}
    >
      {error ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>
          <AlertCircle size={15} /> {error}
        </div>
      ) : null}

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text)', marginBottom: 6 }}>
        <Sparkles size={14} style={{ color: 'var(--accent)' }} /> Suggestions (code CH de l’affaire)
      </div>
      <div style={{ border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-sm)', marginBottom: 18 }}>
        {loading && !info ? (
          <div style={{ padding: 12, fontSize: 13, color: 'var(--text-muted)', display: 'flex', gap: 6, alignItems: 'center' }}>
            <Loader size={14} className="spin" /> Chargement du catalogue Gescom…
          </div>
        ) : suggestions.length === 0 ? (
          <div style={{ padding: 12, fontSize: 13, color: 'var(--text-muted)' }}>
            Aucun projet Gescom ne porte le code CH de cette affaire : utilisez la recherche.
          </div>
        ) : (
          suggestions.map((p) => (
            <ProjectRow key={p.gescomProjectId} p={p} currentId={currentId} affaireId={affaire.id} saving={saving} onPick={pick} />
          ))
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
          <Search size={14} style={{ position: 'absolute', left: 9, top: 9, color: 'var(--text-muted)' }} />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un projet (libellé, code CH, n° Gescom…)"
            style={{
              width: '100%', boxSizing: 'border-box', padding: '7px 10px 7px 28px',
              borderRadius: 'var(--radius-sm)', border: '1px solid var(--glass-border)',
              background: 'var(--card-bg)', color: 'var(--text)', fontSize: 13,
            }}
          />
        </div>
        <button type="button" style={pill(includeInactive)} onClick={() => setIncludeInactive((v) => !v)}>
          Inclure les inactifs
        </button>
        <button
          type="button" title="Relire le catalogue Gescom" onClick={() => load({ refresh: true })}
          style={{ ...pill(false), display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          <RefreshCw size={12} /> Actualiser
        </button>
      </div>
      {query.trim() ? (
        <div style={{ border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-sm)', maxHeight: 320, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: 12, fontSize: 13, color: 'var(--text-muted)' }}>Recherche…</div>
          ) : results.length === 0 ? (
            <div style={{ padding: 12, fontSize: 13, color: 'var(--text-muted)' }}>
              Aucun projet{includeInactive ? '' : ' actif'} ne correspond.
            </div>
          ) : (
            results.map((p) => (
              <ProjectRow key={p.gescomProjectId} p={p} currentId={currentId} affaireId={affaire.id} saving={saving} onPick={pick} />
            ))
          )}
        </div>
      ) : null}
    </Modal>
  );
}
